import React, { RefObject, useEffect } from 'react';
import SvgIcon from 'src/react_components/svg-icon';

export type CommentNavigationBarProps = {
  currentIndex: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onSelectIndex: (index: number) => void;
  // Scopes the up/down arrow keys (see handleKeyDown). Left unset, they are unscoped.
  panelRef?: RefObject<HTMLElement>;
};

// Past this many, dots wrap into noise that eats the panel's height — the counter carries on from there.
const MAX_RAIL_DOTS = 12;

// Native selects and ARIA widgets that emulate one also consume arrow keys (e.g. the sort dropdown above the deck).
const ARROW_KEY_CONSUMERS = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="textbox"]',
  '[role="spinbutton"]',
  '[role="slider"]',
  '[role="menu"]'
].join(', ');

const consumesArrowKeys = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement;
  return !!element?.closest?.(ARROW_KEY_CONSUMERS);
};

const CommentNavigationBar = ({
  currentIndex, total, hasPrevious, hasNext, onPrevious, onNext, onSelectIndex, panelRef
}: CommentNavigationBarProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.altKey || event.ctrlKey || event.metaKey || consumesArrowKeys(event.target)) return;

      // Up/down is the page's own scroll gesture, so only claim it while focus is inside the feedback panel.
      const isVerticalKey = event.key === 'ArrowUp' || event.key === 'ArrowDown';
      if (isVerticalKey && panelRef && !panelRef.current?.contains(document.activeElement)) return;

      if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && hasPrevious) {
        event.preventDefault();
        onPrevious();
      } else if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && hasNext) {
        event.preventDefault();
        onNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasPrevious, hasNext, onPrevious, onNext, panelRef]);

  return (
    <div
      className="comment-navigation-bar d-flex align-items-center justify-content-center gap-3 py-2"
      role="group"
      aria-label="Feedback navigation"
    >
      <button
        type="button"
        className="btn btn-outline-primary comment-navigation-bar-btn comment-navigation-bar-btn--prev"
        onClick={onPrevious}
        disabled={!hasPrevious}
        aria-label="Previous feedback"
      >
        <SvgIcon icon="chevron-left" aria-hidden />
      </button>
      <div className="d-flex flex-column align-items-center gap-1">
        {total <= MAX_RAIL_DOTS && (
          <div className="comment-navigation-rail" role="group" aria-label="Jump to feedback">
            {Array.from({ length: total }, (_, index) => (
              <button
                key={index}
                type="button"
                className={`comment-navigation-rail-dot${index === currentIndex - 1 ? ' active' : ''}`}
                onClick={() => onSelectIndex(index)}
                aria-label={`Go to feedback ${index + 1} of ${total}`}
                aria-current={index === currentIndex - 1 ? 'true' : undefined}
              />
            ))}
          </div>
        )}
        <span aria-live="polite">
          {currentIndex}
          {' of '}
          {total}
        </span>
      </div>
      <button
        type="button"
        className="btn btn-primary comment-navigation-bar-btn comment-navigation-bar-btn--next"
        onClick={onNext}
        disabled={!hasNext}
        aria-label="Next feedback"
      >
        <SvgIcon icon="chevron-right" aria-hidden />
      </button>
    </div>
  );
};

export default CommentNavigationBar;
